/**
 * Edits an account's label, currency and default; its identity never changes. Only what changed is
 * sent, since the editor of a change may not give the second signature.
 */
import { useState } from 'react';
import { Checkbox, CurrencySelector, FormModal, Input, InputWrapper } from '@insio/ui-toolkit';
import type { CurrencyCode, UpdatePartyBankAccountInput } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useBankAccountMutations } from '../hooks/use-bank-account-mutations';
import type { PartyBankAccount } from '../hooks/use-party-bank-accounts';
import { isTerminalState } from '../utils/bank-account-actions';
import { bankAccountError, isStaleViewRefusal } from '../utils/bank-account-error';
import { LABEL_MAX_LENGTH } from '../utils/bank-account-form';
import { BankAccountSummary } from './BankAccountSummary';

interface EditBankAccountModalProps {
  account: PartyBankAccount;
  onClose: () => void;
}

export function EditBankAccountModal({ account, onClose }: EditBankAccountModalProps) {
  const t = useFeatureTranslation('partyBankAccounts.form');
  const tErrors = useFeatureTranslation('partyBankAccounts.errors');
  const tToast = useFeatureTranslation('partyBankAccounts.toast');
  const { showSuccess } = useToastHelpers();
  const { update, refresh } = useBankAccountMutations();

  const [label, setLabel] = useState(account.label ?? '');
  const [currencyCode, setCurrencyCode] = useState(account.currencyCode ?? '');
  const [isDefault, setIsDefault] = useState(account.isDefault);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A rejected or revoked account is never a default.
  const canBeDefault = !isTerminalState(account.verificationState);

  const handleSubmit = async () => {
    setError(null);
    const input: UpdatePartyBankAccountInput = { id: account.id };
    const nextLabel = label.trim() || null;
    // The value comes from the currency picker; the backend's enum check is the guard.
    const nextCurrency = (currencyCode || null) as CurrencyCode | null;
    if (nextLabel !== account.label) input.label = nextLabel;
    if (nextCurrency !== account.currencyCode) input.currencyCode = nextCurrency;
    if (isDefault !== account.isDefault) input.isDefault = isDefault;
    if (Object.keys(input).length === 1) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await update(input);
      showSuccess(tToast('updated'));
      onClose();
    } catch (err) {
      const refusal = bankAccountError(err);
      // The account changed meanwhile: the list shows it as it is now.
      if (isStaleViewRefusal(refusal)) await refresh();
      setError(tErrors(refusal));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open
      testId='edit-bank-account-modal'
      title={t('editTitle')}
      submitLabel={t('submitEdit')}
      cancelLabel={t('cancel')}
      loading={saving}
      error={error}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <div className='flex flex-col gap-md'>
        <BankAccountSummary account={account} />

        <InputWrapper label={t('fields.label')} htmlFor='bank-account-edit-label' labelSize='sm'>
          <Input
            id='bank-account-edit-label'
            testId='bank-account-edit-label'
            value={label}
            maxLength={LABEL_MAX_LENGTH}
            onChange={event => setLabel(event.target.value)}
          />
        </InputWrapper>

        <InputWrapper
          label={t('fields.currencyCode')}
          htmlFor='bank-account-edit-currency'
          labelSize='sm'
        >
          <CurrencySelector
            id='bank-account-edit-currency'
            testId='bank-account-edit-currency'
            value={currencyCode}
            unselectedOptionLabel={t('anyCurrency')}
            onChange={setCurrencyCode}
          />
        </InputWrapper>

        {canBeDefault && (
          <Checkbox
            id='bank-account-edit-default'
            testId='bank-account-edit-default'
            label={t('fields.isDefault')}
            checked={isDefault}
            onChange={setIsDefault}
          />
        )}
      </div>
    </FormModal>
  );
}
